/**
 * You — the private side: the capsule, the questionnaire, the journal
 * (REDESIGN.md §7).
 *
 * The sealed capsule first, as the one dark element the design allows, then
 * white rows: the weekly check-in, the journal, the export, and the two things
 * the reference leaves out but the app cannot — notifications and signing out.
 *
 * A note on the copy: the capsule is *sealed*, not encrypted, and the journal
 * lives on our server, not on the phone. Both are genuinely private — there is
 * no admin read path for either — but the words here say exactly that and
 * nothing more. Promising encryption we do not perform would be a lie printed
 * on the most sensitive screen in the app.
 */

import { apiPost, ApiError } from '../js/api.js'
import { emptyState } from '../components/empty.js'
import { renderCountdown, startCountdown } from '../components/countdown.js'
import { openCeremony } from '../components/ceremony.js'
import { icon } from '../js/icons.js'
import { text } from '../js/html.js'
import { formatDay, parseUtc } from '../js/dates.js'

export function renderYou({ user, push, inner }) {
  return `
    <p class="eyebrow">Private space</p>
    <h1 class="h1">You</h1>
    <p class="sub">Only you can see this. There is no admin screen that reads it.</p>

    ${capsuleSection(inner)}
    ${questionnaireCard(inner)}
    ${checkinCard(inner)}
    ${journalCard(inner)}
    ${dataCard(inner)}

    <section class="row">
      
      ${notificationsBlock(push)}
    </section>

    <section class="row">
      
      <div class="profile-row">
        ${
          user.photo_path
            ? `<img class="avatar" src="/api/uploads/${text(user.photo_path)}" alt="" width="56" height="56" />`
            : `<span class="avatar avatar--empty">${icon('camera', { size: 20 })}</span>`
        }
        <div>
          <p class="profile-row__name">${text(user.name)}</p>
          <p class="profile-row__bio">${user.bio ? text(user.bio) : 'No description.'}</p>
        </div>
      </div>
      <button class="btn btn--quiet" id="logout">
        ${icon('logOut', { size: 18 })}<span>Sign out</span>
      </button>
    </section>
  `
}

// ---------------------------------------------------------------------------
// Capsule
// ---------------------------------------------------------------------------

function capsuleSection(inner) {
  if (inner === null) {
    return '<section class="row"><p class="lede">Loading…</p></section>'
  }

  const capsules = inner.capsules ?? []

  if (capsules.length === 0) {
    return `
      <section class="row">
        <p class="rt">CAPSULE</p>
        ${emptyState({
          icon: 'lock',
          title: 'Nothing sealed yet',
          body: 'A message to yourself, for a day that has not come.',
        })}
        <button class="btn" id="capsule-new">
          ${icon('penLine', { size: 18 })}<span>Write a letter to yourself</span>
        </button>
      </section>
    `
  }

  return capsules.map(capsuleCard).join('')
}

function capsuleCard(capsule) {
  // Unlocked but never opened: the letter is not spilled onto the screen here.
  // It is read in the ceremony, once, with nothing else around it.
  if (capsule.unlocked && !capsule.opened_at) {
    return `
      <section class="row row--arrived">
        <p class="rt">Capsule · Open</p>
        <h4 class="rh">${text(capsule.title ?? 'A letter to yourself')}</h4>
        <p>It has reached its date. Read it somewhere you can be alone for a minute.</p>
        <button class="btn" data-capsule-ceremony="${capsule.id}">
          ${icon('unlock', { size: 17 })}<span>Open it</span>
        </button>
      </section>
    `
  }

  if (capsule.unlocked) {
    return `
      <section class="row row--opened">
        <h4 class="rh">${text(capsule.title ?? 'A message to yourself')}</h4>
        ${
          capsule.video_url
            ? `<video class="capsule__video" src="${text(capsule.video_url)}" controls playsinline
                      preload="metadata"></video>`
            : ''
        }
        ${capsule.body_html ? `<div class="prose">${capsule.body_html}</div>` : ''}
        <button class="btn btn--quiet" data-capsule-ceremony="${capsule.id}">
          ${icon('unlock', { size: 17 })}<span>Read it again</span>
        </button>
      </section>
    `
  }

  // The one dark element the design allows: a sealed chamber in a white lab.
  return `
    <section class="capsule">
      <p class="capsule__tag">
        <span class="capsule__led" aria-hidden="true"></span>Capsule · Sealed
      </p>

      <h3 class="capsule__title">${text(capsule.title ?? 'A letter to yourself')}</h3>
      <p class="capsule__note">
        Written in your own hand, closed until ${formatDay(capsule.locked_until)}.
      </p>

      <div class="countdown-slot" data-countdown="${capsule.locked_until}">
        ${renderCountdown(capsule.locked_until)}
      </div>

      <div class="capsule__bar" aria-hidden="true">
        <i style="width:${progress(capsule)}%"></i>
      </div>

      <p class="capsule__foot">
        ${text(phase(capsule))} · it does not open early, not even for you.
      </p>

      <button class="capsule__edit" data-capsule-edit="${capsule.id}">
        ${icon('penLine', { size: 15 })}<span>Add to it before sealing</span>
      </button>
    </section>
  `
}

/**
 * Which third of the wait this is, named. Derived from the same progress the bar
 * draws — not a stored field, so it cannot claim more than the dates support.
 */
function phase(capsule) {
  const done = progress(capsule)

  if (done < 34) return 'Phase I: Return'
  if (done < 67) return 'Phase II: Quiet anchoring'

  return 'Phase III: Opening'
}

/** How far along the wait is, as a percentage. */
function progress(capsule) {
  if (!capsule.created_at || !capsule.locked_until) {
    return 0
  }

  const start = parseUtc(capsule.created_at).getTime()
  const end = parseUtc(capsule.locked_until).getTime()
  const now = Date.now()

  if (end <= start) {
    return 100
  }

  return Math.max(2, Math.min(100, Math.round(((now - start) / (end - start)) * 100)))
}

// ---------------------------------------------------------------------------
// Questionnaire
// ---------------------------------------------------------------------------

function questionnaireCard(inner) {
  if (inner === null) {
    return ''
  }

  const open = (inner.questionnaires ?? []).filter((q) => !q.locked && q.kind === 'assessment')
  const next = (inner.questionnaires ?? []).find((q) => q.locked)

  if (open.length === 0) {
    return next
      ? `<section class="row">
           <p class="rt">QUESTIONNAIRE</p>
           <h4 class="rh">The first questionnaire is coming</h4>
           <p class="lede">It opens ${formatDay(next.opens_at)}.</p>
         </section>`
      : ''
  }

  return open.map(assessmentBlock).join('')
}

function assessmentBlock(questionnaire) {
  if (!questionnaire.submitted) {
    return `
      <section class="row">
        <p class="rt">QUESTIONNAIRE</p>
        <h4 class="rh">${text(questionnaire.title)}</h4>
        <p class="lede">Fifteen minutes and honesty. There are no right answers.</p>
        <a class="btn" href="/upitnik/${questionnaire.id}" data-link>
          <span>Fill it in</span>${icon('arrowRight', { size: 18 })}
        </a>
      </section>
    `
  }

  if (!questionnaire.result_ready) {
    return `
      <section class="row">
        <p class="rt">QUESTIONNAIRE</p>
        <h4 class="rh">${text(questionnaire.title)}</h4>
        <p class="success-line">${icon('check', { size: 20 })}<span>Submitted.</span></p>
        <p class="lede">
          You will see the result, but not yet${
            questionnaire.reveal_at ? ` — it opens ${formatDay(questionnaire.reveal_at)}` : ''
          }.
        </p>
      </section>
    `
  }

  return `
    <section class="row">
      <p class="rt">QUESTIONNAIRE</p>
      <h4 class="rh">${text(questionnaire.title)}</h4>
      <p class="lede">Your result is ready.</p>
      <a class="btn" href="/upitnik/${questionnaire.id}/rezultat" data-link>
        <span>See the result</span>${icon('arrowRight', { size: 18 })}
      </a>
    </section>
  `
}

// ---------------------------------------------------------------------------
// Weekly check-in — arrows, not a chart
// ---------------------------------------------------------------------------

function checkinCard(inner) {
  const trend = inner?.trend ?? []
  const pending = (inner?.questionnaires ?? []).find(
    (q) => !q.locked && q.kind === 'checkin' && !q.submitted,
  )
  const next = (inner?.questionnaires ?? []).find((q) => q.locked && q.kind === 'checkin')

  if (trend.length === 0 && pending === undefined && next === undefined) {
    return ''
  }

  return `
    <section class="row">
      <p class="rt">EPILOGUE • 7-DAY RHYTHM</p>
      <h4 class="rh">Weekly check-in</h4>

      ${
        pending
          ? `<a class="btn" href="/upitnik/${pending.id}" data-link>
               <span>Fill in ${pending.title.toLowerCase()}</span>${icon('arrowRight', { size: 18 })}
             </a>`
          : ''
      }

      ${trend.length === 0 ? '' : trendGrid(trend)}

      ${
        next
          ? `<p class="next-checkin">
               <span class="next-checkin__when">
                 ${icon('calendar', { size: 16 })}
                 <span>Next reflection ${formatDay(next.opens_at)}</span>
               </span>
             </p>`
          : ''
      }
    </section>
  `
}

function trendGrid(trend) {
  const latest = trend.at(-1)
  const previous = trend.length > 1 ? trend.at(-2) : null

  return `
    <p class="hint">Last ${trend.length} ${trend.length === 1 ? 'week' : 'weeks'}.</p>
    <div class="trend">
      ${latest.dimensions
        .map((dimension) => {
          const before = previous?.dimensions.find((d) => d.key === dimension.key)
          const delta = before === undefined ? null : dimension.value - before.value

          return `
            <div class="trend__row">
              <p class="trend__label">${text(dimension.label)}</p>
              <p class="trend__value">${dimension.value}</p>
              <p class="trend__delta ${deltaClass(delta)}">
                ${deltaIcon(delta)}<span>${deltaLabel(delta)}</span>
              </p>
            </div>
          `
        })
        .join('')}
    </div>
  `
}

function deltaClass(delta) {
  if (delta === null || Math.abs(delta) < 5) return 'trend__delta--flat'

  return delta > 0 ? 'trend__delta--up' : 'trend__delta--down'
}

function deltaIcon(delta) {
  if (delta === null || Math.abs(delta) < 5) return icon('minus', { size: 13 })

  return icon(delta > 0 ? 'trendingUp' : 'trendingDown', { size: 13 })
}

function deltaLabel(delta) {
  if (delta === null) return 'first'
  if (Math.abs(delta) < 5) return 'steady'

  return delta > 0 ? 'u porastu' : 'u padu'
}

// ---------------------------------------------------------------------------
// Journal
// ---------------------------------------------------------------------------

function journalCard(inner) {
  const journal = inner?.journal

  if (journal === undefined) {
    return ''
  }

  const written = (journal.entries ?? []).filter((entry) => entry.id !== null)
  const past = written.filter((entry) => entry.entry_date !== journal.date)

  return `
    <section class="row">
      <div class="card__head">
        <h4 class="rh">Private journal</h4>
        ${
          written.length === 0
            ? ''
            : `<span class="pill pill--quiet">${written.length} ${entriesWord(written.length)}</span>`
        }
      </div>

      <div class="journal-quick">
        <p class="journal-quick__label">${icon('penLine', { size: 15 })}<span>Quick reflection</span></p>
        <textarea class="journal" id="journal" rows="5"
          placeholder="How do you feel right now? Write without censoring…">${text(journal.today?.body_md)}</textarea>

        <div class="journal__foot">
          <label class="journal__context">
            ${icon('mapPin', { size: 15 })}
            <input id="journal-context" type="text" maxlength="80"
              placeholder="Where you are, how it is"
              value="${text(journal.today?.context)}" />
          </label>
          <button class="link-button" id="journal-save">Save entry</button>
        </div>

        <p class="journal__state" id="journal-state"></p>
      </div>

      ${
        past.length === 0
          ? ''
          : `<ul class="journal__list">${past.slice(0, 5).map(journalEntry).join('')}</ul>`
      }

      <p class="confidentiality">
        ${icon('shieldCheck', { size: 20 })}
        <span>
          <strong>Absolute confidentiality</strong>
          These entries are not shared with the hosts or with the group. No screen
          in this app can read them. They are stored on our server in the EU, and
          only you can export them.
        </span>
      </p>
    </section>
  `
}

function entriesWord(count) {
  return count === 1 ? 'entry' : 'entries'
}

function journalEntry(entry) {
  return `
    <li class="journal-entry">
      <p class="journal-entry__top">
        <span class="journal-entry__day">${formatDay(entry.entry_date)}</span>
        <span class="journal-entry__when">${text(entry.tag)}</span>
      </p>
      <p class="journal-entry__preview">${text(entry.preview)}</p>
      <p class="journal-entry__foot">
        <span>${icon('clock', { size: 13 })} ${entry.minutes} min read</span>
        ${entry.context ? `<span>• ${text(entry.context)}</span>` : ''}
      </p>
    </li>
  `
}

// ---------------------------------------------------------------------------
// Data and notifications
// ---------------------------------------------------------------------------

function dataCard(inner) {
  const consents = inner?.consents ?? []

  return `
    <section class="row">
      <p class="rt">YOUR DATA</p>

      <a class="btn btn--quiet" href="/api/me/export" download>
        ${icon('download', { size: 18 })}<span>Download everything</span>
      </a>
      <p class="hint">One JSON file: profile, journal, capsules, answers, deliveries.</p>

      ${
        consents.length === 0
          ? ''
          : `<ul class="consents">
               ${consents
                 .map(
                   (consent) => `<li class="consents__row">
                     <span>${consentLabel(consent.kind)}</span>
                     <span class="consents__state ${consent.revoked_at ? 'consents__state--off' : ''}">
                       ${consent.revoked_at ? 'withdrawn' : 'given'} · ${text(consent.text_version)}
                     </span>
                   </li>`,
                 )
                 .join('')}
             </ul>`
      }

      <p class="hint">
        To delete your account, write to retreat@differlx.com.
        <a href="/privatnost" data-link>Privacy policy</a>
      </p>
    </section>
  `
}

function consentLabel(kind) {
  return { privacy: 'Privacy', push: 'Notifications', gallery: 'Gallery' }[kind] ?? kind
}

function notificationsBlock(push) {
  const onPush = (push?.subscriptions ?? 0) > 0

  if (onPush) {
    return `
      <p class="success-line">${icon('bell', { size: 20 })}<span>Arriving on this device.</span></p>
      <button class="btn btn--quiet" id="push-off">Turn notifications off</button>
    `
  }

  return `
    <p class="reason">${icon('bell', { size: 20 })}<span>
      Everything reaches you by email right now. Push is faster and does not land in spam.
    </span></p>
    ${push?.problem ? `<p class="notice notice--error">${text(push.problem)}</p>` : ''}
    <button class="btn btn--quiet" id="push-on">Turn notifications on</button>
  `
}

// ---------------------------------------------------------------------------
// Behaviour
// ---------------------------------------------------------------------------

const JOURNAL_SAVE_DELAY = 800

/**
 * Wires the journal's auto-save, the capsule editor and the countdown.
 * Called after the screen is in the DOM.
 */
export function mountYou(root, { inner, reload }) {
  if (inner === null) {
    return
  }

  mountJournal(root, inner)
  mountCapsules(root, inner, reload)
  mountCountdowns(root, reload)
}

function mountJournal(root, inner) {
  const field = root.querySelector('#journal')
  const context = root.querySelector('#journal-context')
  const state = root.querySelector('#journal-state')

  if (field === null) {
    return
  }

  let timer = null
  let saved = { body: field.value, context: context?.value ?? '' }

  const save = async () => {
    const current = { body: field.value, context: context?.value ?? '' }

    if (current.body === saved.body && current.context === saved.context) {
      return
    }

    state.textContent = 'Saving…'

    try {
      await apiPost('/journal', {
        entry_date: inner.journal?.date,
        body_md: current.body,
        context: current.context,
      })
      saved = current
      state.textContent = 'Saved.'
    } catch {
      state.textContent = 'Not saved. We will try again.'
    }
  }

  const queue = () => {
    state.textContent = ''
    window.clearTimeout(timer)
    timer = window.setTimeout(save, JOURNAL_SAVE_DELAY)
  }

  field.addEventListener('input', queue)
  context?.addEventListener('input', queue)

  // Leaving the screen or closing the tab must not lose what was typed.
  field.addEventListener('blur', save)
  context?.addEventListener('blur', save)
  root.querySelector('#journal-save')?.addEventListener('click', save)
  window.addEventListener('pagehide', save, { once: true })
}

function mountCountdowns(root, reload) {
  root.querySelectorAll('[data-countdown]').forEach((slot) => {
    startCountdown(slot, slot.dataset.countdown, reload)
  })
}

function mountCapsules(root, inner, reload) {
  root.querySelector('#capsule-new')?.addEventListener('click', (event) => {
    openEditor(event.target.closest('.row'), null, inner, reload)
  })

  root.querySelectorAll('[data-capsule-edit]').forEach((button) => {
    button.addEventListener('click', () => {
      const capsule = inner.capsules.find((item) => item.id === Number(button.dataset.capsuleEdit))
      openEditor(button.closest('.capsule, .row'), capsule, inner, reload)
    })
  })

  root.querySelectorAll('[data-capsule-ceremony]').forEach((button) => {
    button.addEventListener('click', () => {
      const capsule = inner.capsules.find(
        (item) => item.id === Number(button.dataset.capsuleCeremony),
      )

      if (capsule === undefined) return

      openCeremony(capsule, {
        onOpened: () => {
          // Recorded when the letter is actually on screen, not when the
          // button was pressed — a ceremony closed early was not read.
          if (capsule.opened_at) return

          apiPost(`/capsules/${capsule.id}/open`).then(reload, () => {})
        },
      })
    })
  })
}

/** The editor is appended to the card rather than replacing the screen. */
function openEditor(card, capsule, inner, reload) {
  if (card.querySelector('.capsule-editor') !== null) {
    return
  }

  const defaultDate = capsule?.locked_until
    ? capsule.locked_until.slice(0, 10)
    : new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10)

  const maxMb = Math.floor((inner.maxVideoBytes ?? 0) / 1048576)

  const editor = document.createElement('form')
  editor.className = 'capsule-editor'
  editor.innerHTML = `
    <label class="field">
      <span class="field__label">Title</span>
      <input class="field__input" name="title" maxlength="120"
             value="${text(capsule?.title)}" placeholder="To myself in 90 days" />
    </label>

    <label class="field">
      <span class="field__label">Opens on</span>
      <input class="field__input" type="date" name="locked_until" value="${defaultDate}" required />
    </label>

    <label class="field">
      <span class="field__label">Letter</span>
      <textarea class="field__input" name="content_text" rows="6"
        placeholder="Where you are, what you expect, what weighs on you.">${text(capsule?.content_text)}</textarea>
    </label>

    <label class="field">
      <span class="field__label">Or record a video</span>
      <input class="field__input" type="file" name="video" accept="video/*" capture="user" />
      <span class="hint">${maxMb} MB at most — roughly
        ${Math.max(1, Math.round(maxMb / 12))} ${maxMb >= 24 ? 'minutes' : 'minute'} from a phone.
        If yours is longer, record a shorter one.</span>
    </label>

    <div class="admin-form__actions">
      <button class="btn" type="submit">Seal it</button>
      <button class="btn btn--quiet" type="button" data-cancel>Cancel</button>
    </div>
    <p class="notice notice--error capsule-editor__error" hidden></p>
  `

  card.append(editor)

  editor.querySelector('[data-cancel]').addEventListener('click', () => editor.remove())
  editor.addEventListener('submit', (event) => submitCapsule(event, capsule, inner, reload))
}

async function submitCapsule(event, capsule, inner, reload) {
  event.preventDefault()

  const form = event.target
  const problem = form.querySelector('.capsule-editor__error')
  const data = new FormData(form)
  const video = data.get('video')
  const text = String(data.get('content_text') ?? '').trim()

  const fail = (message) => {
    problem.textContent = message
    problem.hidden = false
  }

  problem.hidden = true

  if (video instanceof File && video.size > 0 && video.size > inner.maxVideoBytes) {
    fail(
      `The video is ${(video.size / 1048576).toFixed(0)} MB and the limit is ` +
        `${Math.floor(inner.maxVideoBytes / 1048576)} MB. Record a shorter one.`,
    )

    return
  }

  const hasVideo = video instanceof File && video.size > 0

  if (!hasVideo && text === '') {
    fail('Write something, or record a video.')

    return
  }

  const payload = new FormData()
  payload.append('title', String(data.get('title') ?? ''))
  payload.append('locked_until', String(data.get('locked_until') ?? ''))

  if (capsule !== null) {
    payload.append('capsule_id', String(capsule.id))
  }

  if (hasVideo) {
    payload.append('video', video)
  } else {
    payload.append('content_text', text)
  }

  form.querySelector('[type="submit"]').disabled = true

  try {
    await apiPost('/capsules', payload)
    reload()
  } catch (error) {
    fail(error instanceof ApiError ? error.message : 'Spremanje nije uspjelo.')
    form.querySelector('[type="submit"]').disabled = false
  }
}
